import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Users, Package, Flag, BarChart3, Image as ImageIcon, Shield, Ban, Trash2, Check, Loader2, Eye, AlertTriangle
} from 'lucide-react';

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [listings, setListings] = useState([]);
  const [reports, setReports] = useState([]);
  const [banners, setBanners] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bannerForm, setBannerForm] = useState({ title: '', image_url: '', link_url: '', placement: 'homepage' });

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [u, l, r, b, a] = await Promise.all([
        api.get('/admin/users'), api.get('/admin/listings'), api.get('/admin/reports'),
        api.get('/admin/banners'), api.get('/admin/analytics')
      ]);
      setUsers(u.data.users);
      setListings(l.data.listings);
      setReports(r.data);
      setBanners(b.data);
      setAnalytics(a.data);
    } catch {} finally {
      setLoading(false);
    }
  };

  const handleBan = async (userId) => {
    try {
      const { data } = await api.put(`/admin/users/${userId}/ban`);
      setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, is_banned: data.is_banned } : u));
      toast.success(data.is_banned ? 'User banned' : 'User unbanned');
    } catch {
      toast.error('Failed');
    }
  };

  const handleRemoveListing = async (listingId) => {
    try {
      await api.delete(`/admin/listings/${listingId}`);
      setListings(prev => prev.map(l => l.listing_id === listingId ? { ...l, status: 'removed' } : l));
      toast.success('Listing removed');
    } catch {
      toast.error('Failed');
    }
  };

  const handleResolveReport = async (reportId) => {
    try {
      await api.put(`/admin/reports/${reportId}`);
      setReports(prev => prev.map(r => r.report_id === reportId ? { ...r, status: 'resolved' } : r));
      toast.success('Report resolved');
    } catch {
      toast.error('Failed');
    }
  };

  const handleCreateBanner = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post('/admin/banners', bannerForm);
      setBanners(prev => [...prev, data]);
      setBannerForm({ title: '', image_url: '', link_url: '', placement: 'homepage' });
      toast.success('Banner created');
    } catch {
      toast.error('Failed');
    }
  };

  const handleDeleteBanner = async (bannerId) => {
    try {
      await api.delete(`/admin/banners/${bannerId}`);
      setBanners(prev => prev.filter(b => b.banner_id !== bannerId));
      toast.success('Banner deleted');
    } catch {
      toast.error('Failed');
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="min-h-screen" data-testid="admin-panel">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#2C362F]" style={{ fontFamily: 'Outfit, sans-serif' }}>Admin Panel</h1>
        </div>

        <Tabs defaultValue="analytics" className="space-y-6">
          <TabsList className="bg-[#F4F1EA] p-1 rounded-xl">
            <TabsTrigger value="analytics" className="rounded-lg gap-1.5" data-testid="admin-analytics-tab"><BarChart3 className="w-4 h-4" />Analytics</TabsTrigger>
            <TabsTrigger value="users" className="rounded-lg gap-1.5" data-testid="admin-users-tab"><Users className="w-4 h-4" />Users</TabsTrigger>
            <TabsTrigger value="listings" className="rounded-lg gap-1.5" data-testid="admin-listings-tab"><Package className="w-4 h-4" />Listings</TabsTrigger>
            <TabsTrigger value="reports" className="rounded-lg gap-1.5" data-testid="admin-reports-tab"><Flag className="w-4 h-4" />Reports</TabsTrigger>
            <TabsTrigger value="banners" className="rounded-lg gap-1.5" data-testid="admin-banners-tab"><ImageIcon className="w-4 h-4" />Banners</TabsTrigger>
          </TabsList>

          {/* Analytics */}
          <TabsContent value="analytics">
            {analytics && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Total Users', value: analytics.total_users, color: 'text-primary' },
                  { label: 'Active Listings', value: analytics.total_listings, color: 'text-[#7D9B8B]' },
                  { label: 'Messages', value: analytics.total_messages, color: 'text-[#E5B66A]' },
                  { label: 'Pending Reports', value: analytics.pending_reports, color: 'text-destructive' },
                  { label: 'For Sale', value: analytics.categories?.sale || 0, color: 'text-primary' },
                  { label: 'Free Items', value: analytics.categories?.free || 0, color: 'text-[#E5B66A]' },
                  { label: 'Lending', value: analytics.categories?.lending || 0, color: 'text-[#7D9B8B]' },
                  { label: 'New Users (7d)', value: analytics.new_users_this_week, color: 'text-primary' },
                ].map((stat, i) => (
                  <div key={i} className="bg-white border border-[#E8E6DF] rounded-2xl p-5">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">{stat.label}</p>
                    <p className={`text-3xl font-bold mt-1 ${stat.color}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{stat.value}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Users */}
          <TabsContent value="users">
            <div className="bg-white border border-[#E8E6DF] rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b border-[#E8E6DF] bg-[#F4F1EA]">
                    <th className="text-left text-xs font-semibold text-muted-foreground p-3 uppercase tracking-wider">User</th>
                    <th className="text-left text-xs font-semibold text-muted-foreground p-3 uppercase tracking-wider">Email</th>
                    <th className="text-left text-xs font-semibold text-muted-foreground p-3 uppercase tracking-wider">Role</th>
                    <th className="text-left text-xs font-semibold text-muted-foreground p-3 uppercase tracking-wider">Status</th>
                    <th className="text-right text-xs font-semibold text-muted-foreground p-3 uppercase tracking-wider">Actions</th>
                  </tr></thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.user_id} className="border-b border-[#E8E6DF] last:border-0">
                        <td className="p-3 text-sm font-medium">{u.name || 'Unnamed'}</td>
                        <td className="p-3 text-sm text-muted-foreground">{u.email}</td>
                        <td className="p-3"><Badge className={`rounded-full text-[10px] ${u.role === 'admin' ? 'bg-primary text-white' : 'bg-[#F4F1EA] text-[#2C362F]'} border-0`}>{u.role}</Badge></td>
                        <td className="p-3">
                          {u.is_banned ? <Badge className="rounded-full text-[10px] bg-destructive/10 text-destructive border-0">Banned</Badge> : <Badge className="rounded-full text-[10px] bg-[#7D9B8B]/10 text-[#7D9B8B] border-0">Active</Badge>}
                        </td>
                        <td className="p-3 text-right">
                          {u.role !== 'admin' && (
                            <Button variant="ghost" size="sm" className="rounded-lg text-xs" onClick={() => handleBan(u.user_id)} data-testid={`ban-user-${u.user_id}`}>
                              <Ban className="w-3.5 h-3.5 mr-1" />{u.is_banned ? 'Unban' : 'Ban'}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Listings */}
          <TabsContent value="listings">
            <div className="bg-white border border-[#E8E6DF] rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b border-[#E8E6DF] bg-[#F4F1EA]">
                    <th className="text-left text-xs font-semibold text-muted-foreground p-3 uppercase tracking-wider">Title</th>
                    <th className="text-left text-xs font-semibold text-muted-foreground p-3 uppercase tracking-wider">Category</th>
                    <th className="text-left text-xs font-semibold text-muted-foreground p-3 uppercase tracking-wider">User</th>
                    <th className="text-left text-xs font-semibold text-muted-foreground p-3 uppercase tracking-wider">Status</th>
                    <th className="text-right text-xs font-semibold text-muted-foreground p-3 uppercase tracking-wider">Actions</th>
                  </tr></thead>
                  <tbody>
                    {listings.map(l => (
                      <tr key={l.listing_id} className="border-b border-[#E8E6DF] last:border-0">
                        <td className="p-3 text-sm font-medium max-w-[200px] truncate">{l.title}</td>
                        <td className="p-3"><Badge className="rounded-full text-[10px] bg-[#F4F1EA] text-[#2C362F] border-0">{l.category}</Badge></td>
                        <td className="p-3 text-sm text-muted-foreground">{l.user_name}</td>
                        <td className="p-3">
                          <Badge className={`rounded-full text-[10px] border-0 ${l.status === 'active' ? 'bg-[#7D9B8B]/10 text-[#7D9B8B]' : 'bg-destructive/10 text-destructive'}`}>{l.status}</Badge>
                        </td>
                        <td className="p-3 text-right space-x-1">
                          <Button variant="ghost" size="sm" className="rounded-lg text-xs" onClick={() => navigate(`/listings/${l.listing_id}`)} data-testid={`view-admin-listing-${l.listing_id}`}>
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                          {l.status === 'active' && (
                            <Button variant="ghost" size="sm" className="rounded-lg text-xs text-destructive" onClick={() => handleRemoveListing(l.listing_id)} data-testid={`remove-listing-${l.listing_id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Reports */}
          <TabsContent value="reports">
            {reports.length === 0 ? (
              <div className="text-center py-16 bg-white border border-[#E8E6DF] rounded-2xl">
                <Flag className="w-12 h-12 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
                <p className="text-muted-foreground">No reports</p>
              </div>
            ) : (
              <div className="space-y-3">
                {reports.map(r => (
                  <div key={r.report_id} className="bg-white border border-[#E8E6DF] rounded-2xl p-4 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <AlertTriangle className="w-4 h-4 text-[#E5B66A]" strokeWidth={1.5} />
                        <span className="font-medium text-sm">{r.reason}</span>
                        <Badge className={`rounded-full text-[10px] border-0 ${r.status === 'pending' ? 'bg-[#E5B66A]/10 text-[#E5B66A]' : 'bg-[#7D9B8B]/10 text-[#7D9B8B]'}`}>{r.status}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">Type: {r.target_type} | By: {r.reporter_name} | {new Date(r.created_at).toLocaleDateString()}</p>
                      {r.description && <p className="text-sm text-foreground/80 mt-1">{r.description}</p>}
                    </div>
                    {r.status === 'pending' && (
                      <Button variant="ghost" size="sm" className="rounded-lg gap-1 text-[#7D9B8B]" onClick={() => handleResolveReport(r.report_id)} data-testid={`resolve-report-${r.report_id}`}>
                        <Check className="w-3.5 h-3.5" />Resolve
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Banners */}
          <TabsContent value="banners">
            <div className="bg-white border border-[#E8E6DF] rounded-2xl p-6 mb-4">
              <h3 className="font-semibold text-[#2C362F] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Create Banner Ad</h3>
              <form onSubmit={handleCreateBanner} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Title</Label>
                  <Input value={bannerForm.title} onChange={e => setBannerForm({ ...bannerForm, title: e.target.value })} className="rounded-xl bg-[#FDFBF7] border-[#E8E6DF] h-10" required data-testid="banner-title-input" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Image URL</Label>
                  <Input value={bannerForm.image_url} onChange={e => setBannerForm({ ...bannerForm, image_url: e.target.value })} className="rounded-xl bg-[#FDFBF7] border-[#E8E6DF] h-10" required data-testid="banner-image-input" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Link URL</Label>
                  <Input value={bannerForm.link_url} onChange={e => setBannerForm({ ...bannerForm, link_url: e.target.value })} className="rounded-xl bg-[#FDFBF7] border-[#E8E6DF] h-10" data-testid="banner-link-input" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Placement</Label>
                  <Input value={bannerForm.placement} onChange={e => setBannerForm({ ...bannerForm, placement: e.target.value })} className="rounded-xl bg-[#FDFBF7] border-[#E8E6DF] h-10" data-testid="banner-placement-input" />
                </div>
                <Button type="submit" className="rounded-xl bg-primary text-white sm:col-span-2" data-testid="create-banner-btn">Create Banner</Button>
              </form>
            </div>

            {banners.length > 0 && (
              <div className="space-y-3">
                {banners.map(b => (
                  <div key={b.banner_id} className="bg-white border border-[#E8E6DF] rounded-2xl p-4 flex items-center gap-4">
                    {b.image_url && <img src={b.image_url} alt="" className="w-20 h-14 rounded-lg object-cover" />}
                    <div className="flex-1">
                      <p className="font-medium text-sm">{b.title}</p>
                      <p className="text-xs text-muted-foreground">Placement: {b.placement} | Clicks: {b.clicks}</p>
                    </div>
                    <Button variant="ghost" size="sm" className="rounded-lg text-destructive" onClick={() => handleDeleteBanner(b.banner_id)} data-testid={`delete-banner-${b.banner_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
